import { useEffect, useState } from 'react';
import {
  api, type ComedyChannel, type ComedyResearchItem, type ComedySegment, type ComedySource,
} from '../../lib/api';
import { Button, ErrorAlert, Spinner } from '../../components/ui';
import {
  ComedyEmpty, ComedySection, ComedyStatus, Field, formatDate, formatMs, splitList,
} from './ComedyShared';

const RIGHTS_STATUS = ['UNKNOWN', 'METADATA_ONLY', 'CLEARED', 'RESTRICTED', 'REJECTED'];
const RIGHTS_BASIS = ['UNVERIFIED', 'OWNED', 'LICENSED', 'CREATIVE_COMMONS', 'PUBLIC_DOMAIN', 'PLATFORM_TERMS'];
const SOURCE_KINDS = ['YOUTUBE_SEARCH', 'YOUTUBE_CHANNEL', 'RSS', 'ATOM', 'WIKIMEDIA_COMMONS', 'INTERNET_ARCHIVE', 'MANUAL'];

export function ComedyResearchPanel({ channel }: { channel: ComedyChannel }) {
  const [items, setItems] = useState<ComedyResearchItem[]>([]);
  const [total, setTotal] = useState(0);
  const [filter, setFilter] = useState({ status: '', rights: '', q: '' });
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [message, setMessage] = useState('');
  const [reviewing, setReviewing] = useState<ComedyResearchItem | null>(null);
  const [sourceWarnings, setSourceWarnings] = useState<ComedySource[]>([]);

  const load = async () => {
    setLoading(true); setError(null);
    try {
      const [response, sourceResponse] = await Promise.all([
        api.comedy.researchItems(channel.id, filter),
        api.comedy.sources(channel.id),
      ]);
      setItems(response.items); setTotal(response.total);
      setSourceWarnings(sourceResponse.sources.filter((source) => source.enabled && (source.status === 'ERROR' || Boolean(source.lastError))));
    } catch (err) { setError(err); } finally { setLoading(false); }
  };
  useEffect(() => { void load(); }, [channel.id, filter.status, filter.rights]);

  const run = async (sourceId?: string) => {
    setBusy(true); setError(null); setMessage('');
    try {
      const result = await api.comedy.research(channel.id, { maxSources: sourceId ? 1 : 3, ...(sourceId ? { sourceId } : {}) });
      setMessage(`Research zakończony: dodano ${result.inserted} nowych materiałów.`);
      await load();
    } catch (err) { setError(err); } finally { setBusy(false); }
  };

  const importItem = async (item: ComedyResearchItem) => {
    setBusy(true); setError(null); setMessage('');
    try {
      const result = await api.comedy.importResearchItem(item.id);
      setMessage(result.queued ? 'Pobieranie materiału zakolejkowane na VPS. Po pobraniu uruchom transkrypcję w Bibliotece.' : 'Materiał był już pobrany.');
      await load();
    } catch (err) { setError(err); } finally { setBusy(false); }
  };

  return (
    <div style={{ display: 'grid', gap: 16 }}>
      <ComedySection
        title="Research AI"
        description="Odkrywanie wypowiedzi z YouTube Data API, podcastów RSS/Atom, Wikimedia Commons, Internet Archive i źródeł ręcznych. Publiczna dostępność nie jest zgodą na ponowne użycie."
        actions={<div className="btn-row"><Button size="sm" onClick={() => void run()} disabled={busy}>{busy ? 'Trwa research…' : 'Uruchom research'}</Button><Button size="sm" onClick={() => void load()}>Odśwież</Button></div>}
      >
        {error ? <ErrorAlert error={error} /> : null}
        {message ? <div className="alert alert--success">{message}</div> : null}
        {sourceWarnings.length > 0 && (
          <div className="alert alert--warning">
            <strong>Źródła wymagają konfiguracji:</strong>
            {sourceWarnings.map((source) => (
              <div key={source.id}>{source.name} ({source.kind}): {source.lastError ?? source.status}</div>
            ))}
            <div style={{ marginTop: 6 }}>Kliknij „Test” w zakładce Źródła materiałów, aby sprawdzić źródło. Dla YouTube ustaw sekret <code>YOUTUBE_API_KEY</code>.</div>
          </div>
        )}
        <div className="form-row">
          <Field label="Szukaj w wynikach"><input value={filter.q} onChange={(event) => setFilter((current) => ({ ...current, q: event.target.value }))} onKeyDown={(event) => { if (event.key === 'Enter') void load(); }} placeholder="np. wywiad, absurd" /></Field>
          <div className="form-row">
            <Field label="Status analizy"><select value={filter.status} onChange={(event) => setFilter((current) => ({ ...current, status: event.target.value }))}><option value="">wszystkie</option><option value="PENDING">PENDING</option><option value="QUEUED">QUEUED</option><option value="TRANSCRIBING">TRANSCRIBING</option><option value="ANALYZED">ANALYZED</option><option value="FAILED">FAILED</option></select></Field>
            <Field label="Prawa"><select value={filter.rights} onChange={(event) => setFilter((current) => ({ ...current, rights: event.target.value }))}><option value="">wszystkie</option>{RIGHTS_STATUS.map((status) => <option key={status} value={status}>{status}</option>)}</select></Field>
          </div>
        </div>
        <div className="muted" style={{ marginBottom: 8 }}>{total} materiałów w tym kanale. Materiały bez CLEARED trafiają wyłącznie do researchu — nie do produkcji.</div>
        {loading ? <Spinner label="Ładowanie materiałów…" /> : items.length === 0 ? (
          <ComedyEmpty title="Brak materiałów" hint="Uruchom research lub dodaj własne źródło RSS. Możesz też wgrać własny plik w Bibliotece." />
        ) : (
          <div className="table-wrap" style={{ overflowX: 'auto' }}>
            <table className="table">
              <thead><tr><th>Tytuł</th><th>Źródło</th><th>Język</th><th>Czas</th><th>Ocena</th><th>Prawa</th><th>Pobranie</th><th>Analiza</th><th /></tr></thead>
              <tbody>
                {items.map((item) => (
                  <tr key={item.id}>
                    <td style={{ maxWidth: 360 }}>
                      <strong>{item.title}</strong>
                      <div className="muted" style={{ fontSize: 11, overflowWrap: 'anywhere' }}>{item.author ?? '—'} · <a href={item.url} target="_blank" rel="noreferrer">źródło</a></div>
                    </td>
                    <td>{item.sourceId ? 'źródło kanału' : 'ręczne'}<div className="muted" style={{ fontSize: 11 }}>{formatDate(item.publishedAt, false)}</div></td>
                    <td>{item.language ?? '—'}</td>
                    <td>{item.durationSeconds ? `${Math.round(item.durationSeconds)} s` : '—'}</td>
                    <td>{Math.round(item.relevanceScore)}</td>
                    <td><ComedyStatus value={item.licenseStatus} /><div className="muted" style={{ fontSize: 11 }}>{item.rightsBasis}</div></td>
                    <td><ComedyStatus value={item.downloadStatus} /></td>
                    <td><ComedyStatus value={item.analysisStatus} /></td>
                    <td>
                      <div className="btn-row">
                        <Button size="sm" onClick={() => setReviewing(item)}>Oceń prawa</Button>
                        {item.licenseStatus === 'CLEARED' && item.downloadStatus !== 'DOWNLOADED' && <Button size="sm" variant="primary" onClick={() => void importItem(item)} disabled={busy}>Pobierz</Button>}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </ComedySection>
      {reviewing && <RightsReviewModal item={reviewing} onClose={() => setReviewing(null)} onSaved={() => { setReviewing(null); void load(); }} />}
    </div>
  );
}

function RightsReviewModal({ item, onClose, onSaved }: { item: ComedyResearchItem; onClose: () => void; onSaved: () => void }) {
  const [licenseStatus, setLicenseStatus] = useState(item.licenseStatus);
  const [rightsBasis, setRightsBasis] = useState(item.rightsBasis);
  const [commercialUse, setCommercialUse] = useState(item.commercialUse ?? false);
  const [modificationsAllowed, setModificationsAllowed] = useState(item.modificationsAllowed ?? false);
  const [attributionRequired, setAttributionRequired] = useState(item.attributionRequired);
  const [attributionText, setAttributionText] = useState(item.attributionText ?? '');
  const [licenseName, setLicenseName] = useState(item.licenseName ?? '');
  const [licenseUrl, setLicenseUrl] = useState('');
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);

  const save = async () => {
    setBusy(true); setError(null);
    try {
      await api.comedy.reviewRights(item.id, {
        licenseStatus, rightsBasis, commercialUse, modificationsAllowed, attributionRequired,
        attributionText: attributionText || null, licenseName: licenseName || null,
        licenseUrl: licenseUrl || null, notes: notes || null,
      });
      onSaved();
    } catch (err) { setError(err); } finally { setBusy(false); }
  };

  return (
    <div className="modal-overlay" role="dialog" aria-modal="true" onClick={onClose}>
      <div className="modal modal--wide" onClick={(event) => event.stopPropagation()}>
        <div className="modal__header"><div className="modal__title">Ocena praw do materiału</div><button className="modal__close" onClick={onClose}>×</button></div>
        <div className="modal__body">
          <div style={{ marginBottom: 12 }}><strong>{item.title}</strong><div className="muted" style={{ fontSize: 12, overflowWrap: 'anywhere' }}>{item.url}</div></div>
          {error ? <ErrorAlert error={error} /> : null}
          <div className="alert alert--info">Parodia i pastisz zależą od okoliczności i nie oznaczają automatycznego prawa do pobierania dowolnych nagrań. CLEARED wymaga udokumentowanej podstawy.</div>
          <div className="form-row">
            <Field label="Status licencji"><select value={licenseStatus} onChange={(event) => setLicenseStatus(event.target.value)}>{RIGHTS_STATUS.map((status) => <option key={status}>{status}</option>)}</select></Field>
            <Field label="Podstawa wykorzystania"><select value={rightsBasis} onChange={(event) => setRightsBasis(event.target.value)}>{RIGHTS_BASIS.map((basis) => <option key={basis}>{basis}</option>)}</select></Field>
          </div>
          <div className="form-row">
            <Field label="Nazwa licencji"><input value={licenseName} onChange={(event) => setLicenseName(event.target.value)} placeholder="np. CC BY 4.0, umowa licencyjna" /></Field>
            <Field label="URL licencji"><input value={licenseUrl} onChange={(event) => setLicenseUrl(event.target.value)} placeholder="https://…" /></Field>
          </div>
          <div className="grid grid--3" style={{ marginBottom: 12 }}>
            <label className="inline"><input type="checkbox" checked={commercialUse} onChange={(event) => setCommercialUse(event.target.checked)} /> Wykorzystanie komercyjne</label>
            <label className="inline"><input type="checkbox" checked={modificationsAllowed} onChange={(event) => setModificationsAllowed(event.target.checked)} /> Przeróbki / montaż</label>
            <label className="inline"><input type="checkbox" checked={attributionRequired} onChange={(event) => setAttributionRequired(event.target.checked)} /> Wymagana atrybucja</label>
          </div>
          <Field label="Treść atrybucji" hint="Pojawi się w opisie publikacji, jeśli jest wymagana."><input value={attributionText} onChange={(event) => setAttributionText(event.target.value)} /></Field>
          <Field label="Notatka / podstawa prawna"><textarea rows={3} value={notes} onChange={(event) => setNotes(event.target.value)} placeholder="np. własne nagranie kanału, umowa z dnia…, CC BY 4.0" /></Field>
        </div>
        <div className="modal__footer"><Button onClick={onClose}>Anuluj</Button><Button variant="primary" onClick={() => void save()} disabled={busy}>{busy ? 'Zapisywanie…' : 'Zapisz ocenę'}</Button></div>
      </div>
    </div>
  );
}

export function ComedySourcesPanel({ channel }: { channel: ComedyChannel }) {
  const [sources, setSources] = useState<ComedySource[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<unknown>(null);
  const [message, setMessage] = useState('');
  const [editing, setEditing] = useState<ComedySource | null>(null);
  const [form, setForm] = useState({ name: '', kind: 'RSS', url: '', queries: '', query: '', channelId: '', licensePolicy: 'METADATA_ONLY', enabled: true, intervalMinutes: 1440, priority: 50, config: '{}' });
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true); setError(null);
    try { setSources((await api.comedy.sources(channel.id)).sources); } catch (err) { setError(err); } finally { setLoading(false); }
  };
  useEffect(() => { void load(); }, [channel.id]);

  const startCreate = () => {
    setEditing(null);
    setForm({ name: '', kind: 'RSS', url: '', queries: '', query: '', channelId: '', licensePolicy: 'METADATA_ONLY', enabled: true, intervalMinutes: 1440, priority: 50, config: '{}' });
  };
  const startEdit = (source: ComedySource) => {
    setEditing(source);
    const config = source.config ?? {};
    setForm({
      name: source.name, kind: source.kind, url: source.url ?? '', queries: Array.isArray(config.queries) ? (config.queries as string[]).join(', ') : '',
      query: typeof config.query === 'string' ? config.query : '', channelId: typeof config.channelId === 'string' ? config.channelId : '',
      licensePolicy: source.licensePolicy, enabled: source.enabled, intervalMinutes: source.intervalMinutes, priority: source.priority, config: JSON.stringify(config, null, 2),
    });
  };

  const save = async () => {
    setBusy(true); setError(null);
    try {
      let config: Record<string, unknown> = {};
      try { config = JSON.parse(form.config || '{}') as Record<string, unknown>; } catch { throw new Error('Konfiguracja JSON jest niepoprawna'); }
      if (form.queries.trim()) config.queries = splitList(form.queries);
      if (form.query.trim()) config.query = form.query.trim();
      if (form.channelId.trim()) config.channelId = form.channelId.trim();
      const payload = {
        name: form.name, kind: form.kind, url: form.url || null, config,
        licensePolicy: form.licensePolicy, enabled: form.enabled, intervalMinutes: Number(form.intervalMinutes), priority: Number(form.priority),
      };
      if (editing) await api.comedy.updateSource(channel.id, editing.id, payload);
      else await api.comedy.createSource(channel.id, payload);
      setMessage('Źródło zapisane.'); setEditing(null); setForm((current) => ({ ...current, name: '' })); await load();
    } catch (err) { setError(err); } finally { setBusy(false); }
  };

  const test = async (source: ComedySource) => {
    setBusy(true); setError(null); setMessage('');
    try {
      const result = await api.comedy.testSource(channel.id, source.id);
      setMessage(`Test: ${result.discovered} wyników. ${result.sample.map((item) => item.title).slice(0, 3).join(' · ')}`);
    } catch (err) { setError(err); } finally { setBusy(false); }
  };

  const remove = async (source: ComedySource) => {
    if (!window.confirm(`Usunąć źródło „${source.name}”?`)) return;
    setBusy(true);
    try { await api.comedy.deleteSource(channel.id, source.id); await load(); } catch (err) { setError(err); } finally { setBusy(false); }
  };

  return (
    <div style={{ display: 'grid', gap: 16 }}>
      <ComedySection
        title="Źródła materiałów"
        description="Każde źródło ma własną politykę licencji. YouTube służy do odkrywania metadanych; pobieranie jest możliwe tylko dla materiałów z potwierdzonymi prawami i bezpośrednim plikiem."
        actions={<div className="btn-row"><Button size="sm" onClick={startCreate}>+ Nowe źródło</Button><Button size="sm" onClick={() => void load()}>Odśwież</Button></div>}
      >
        {error ? <ErrorAlert error={error} /> : null}
        {message ? <div className="alert alert--success">{message}</div> : null}
        {loading ? <Spinner label="Ładowanie źródeł…" /> : (
          <div className="table-wrap" style={{ overflowX: 'auto' }}>
            <table className="table">
              <thead><tr><th>Nazwa</th><th>Typ</th><th>Polityka</th><th>Interwał</th><th>Ostatni run</th><th>Status</th><th /></tr></thead>
              <tbody>
                {sources.map((source) => (
                  <tr key={source.id}>
                    <td><strong>{source.name}</strong><div className="muted" style={{ fontSize: 11, overflowWrap: 'anywhere' }}>{source.url ?? (Array.isArray(source.config.queries) ? (source.config.queries as string[]).join(', ') : '')}</div></td>
                    <td>{source.kind}</td>
                    <td>{source.licensePolicy}</td>
                    <td>{source.intervalMinutes} min</td>
                    <td>{formatDate(source.lastRunAt)}{source.lastError && <div className="muted" style={{ fontSize: 11, color: 'var(--danger)' }}>{source.lastError.slice(0, 120)}</div>}</td>
                    <td><ComedyStatus value={source.status} /></td>
                    <td><div className="btn-row"><Button size="sm" onClick={() => void test(source)} disabled={busy}>Test</Button><Button size="sm" onClick={() => startEdit(source)}>Edytuj</Button><Button size="sm" variant="danger" onClick={() => void remove(source)}>Usuń</Button></div></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </ComedySection>

      <ComedySection title={editing ? `Edycja źródła: ${editing.name}` : 'Nowe źródło'} description="Dla YouTube Search wpisz zapytania; dla RSS podaj URL feedu podcastu lub kanału.">
        <div className="form-row">
          <Field label="Nazwa"><input value={form.name} onChange={(event) => setForm((current) => ({ ...current, name: event.target.value }))} /></Field>
          <Field label="Typ">
            <select value={form.kind} onChange={(event) => setForm((current) => ({ ...current, kind: event.target.value }))}>{SOURCE_KINDS.map((kind) => <option key={kind}>{kind}</option>)}</select>
          </Field>
        </div>
        <div className="form-row">
          <Field label="URL" hint="Wymagany dla RSS/Atom; opcjonalny dla pozostałych."><input value={form.url} onChange={(event) => setForm((current) => ({ ...current, url: event.target.value }))} placeholder="https://…" /></Field>
          <Field label="Polityka licencji">
            <select value={form.licensePolicy} onChange={(event) => setForm((current) => ({ ...current, licensePolicy: event.target.value }))}>
              {['METADATA_ONLY', 'OWNED', 'LICENSED', 'CREATIVE_COMMONS', 'PUBLIC_DOMAIN'].map((policy) => <option key={policy}>{policy}</option>)}
            </select>
          </Field>
        </div>
        <div className="form-row">
          <Field label="Zapytania YouTube (przecinki)"><input value={form.queries} onChange={(event) => setForm((current) => ({ ...current, queries: event.target.value }))} placeholder="zabawny wywiad, absurdalna odpowiedź" /></Field>
          <Field label="Zapytanie archiwum / Wikimedia"><input value={form.query} onChange={(event) => setForm((current) => ({ ...current, query: event.target.value }))} placeholder="interview" /></Field>
        </div>
        <div className="form-row">
          <Field label="ID kanału YouTube (opcjonalne)" hint="Dla YOUTUBE_CHANNEL — własny lub partnerski kanał."><input value={form.channelId} onChange={(event) => setForm((current) => ({ ...current, channelId: event.target.value }))} /></Field>
          <Field label="Interwał (minuty)"><input type="number" min={0} max={10080} value={form.intervalMinutes} onChange={(event) => setForm((current) => ({ ...current, intervalMinutes: Number(event.target.value) }))} /></Field>
        </div>
        <div className="form-row">
          <Field label="Priorytet (0–100)"><input type="number" min={0} max={100} value={form.priority} onChange={(event) => setForm((current) => ({ ...current, priority: Number(event.target.value) }))} /></Field>
          <Field label="Konfiguracja JSON (zaawansowana)"><textarea rows={3} value={form.config} onChange={(event) => setForm((current) => ({ ...current, config: event.target.value }))} /></Field>
        </div>
        <label className="inline"><input type="checkbox" checked={form.enabled} onChange={(event) => setForm((current) => ({ ...current, enabled: event.target.checked }))} /> Źródło aktywne</label>
        <div className="btn-row" style={{ marginTop: 12 }}>
          <Button variant="primary" size="sm" onClick={() => void save()} disabled={busy || !form.name}>{editing ? 'Zapisz zmiany' : 'Dodaj źródło'}</Button>
          {editing && <Button size="sm" onClick={() => setEditing(null)}>Anuluj edycję</Button>}
        </div>
      </ComedySection>
    </div>
  );
}

export function ComedyLibraryPanel({ channel }: { channel: ComedyChannel }) {
  const [media, setMedia] = useState<Array<Record<string, unknown>>>([]);
  const [segments, setSegments] = useState<ComedySegment[]>([]);
  const [total, setTotal] = useState(0);
  const [query, setQuery] = useState('');
  const [hits, setHits] = useState<Array<ComedySegment & { score: number; matchedTerms: string[] }>>([]);
  const [semantic, setSemantic] = useState(false);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [message, setMessage] = useState('');
  const [upload, setUpload] = useState({ file: null as File | null, title: '', author: '', sourceUrl: '', rightsBasis: 'OWNED', commercialUse: true, modificationsAllowed: true, attributionRequired: false, attributionText: '', speakerName: '' });

  const load = async () => {
    setLoading(true); setError(null);
    try {
      const [mediaResponse, segmentResponse] = await Promise.all([
        api.comedy.media(channel.id),
        api.comedy.segments(channel.id, { limit: 50 }),
      ]);
      setMedia(mediaResponse.media); setSegments(segmentResponse.segments); setTotal(segmentResponse.total);
    } catch (err) { setError(err); } finally { setLoading(false); }
  };
  useEffect(() => { void load(); }, [channel.id]);

  const doUpload = async () => {
    if (!upload.file) { setError(new Error('Wybierz plik audio/wideo.')); return; }
    setBusy(true); setError(null); setMessage('');
    try {
      const form = new FormData();
      form.append('file', upload.file);
      form.append('title', upload.title || upload.file.name);
      if (upload.author) form.append('author', upload.author);
      if (upload.sourceUrl) form.append('sourceUrl', upload.sourceUrl);
      form.append('rightsBasis', upload.rightsBasis);
      form.append('commercialUse', String(upload.commercialUse));
      form.append('modificationsAllowed', String(upload.modificationsAllowed));
      form.append('attributionRequired', String(upload.attributionRequired));
      if (upload.attributionText) form.append('attributionText', upload.attributionText);
      if (upload.speakerName) form.append('speakerName', upload.speakerName);
      await api.comedy.uploadMedia(channel.id, form);
      setMessage('Plik wgrany. Uruchom transkrypcję, aby dodać wypowiedzi do biblioteki.');
      setUpload((current) => ({ ...current, file: null, title: '', author: '', sourceUrl: '', speakerName: '' }));
      await load();
    } catch (err) { setError(err); } finally { setBusy(false); }
  };

  const transcribe = async (mediaId: string) => {
    setBusy(true); setError(null); setMessage('');
    try {
      const result = await api.comedy.transcribe(channel.id, mediaId);
      setMessage(result.status === 'COMPLETED' ? 'Transkrypcja już istnieje.' : 'Transkrypcja zakolejkowana. Lokalny whisper.cpp przetworzy ją na VPS.');
      await load();
    } catch (err) { setError(err); } finally { setBusy(false); }
  };

  const search = async () => {
    if (!query.trim()) { setHits([]); return; }
    setBusy(true); setError(null);
    try {
      const result = await api.comedy.searchSegments(channel.id, { query, limit: 25 });
      setHits(result.hits); setSemantic(result.semantic);
    } catch (err) { setError(err); } finally { setBusy(false); }
  };

  return (
    <div style={{ display: 'grid', gap: 16 }}>
      <ComedySection title="Biblioteka wypowiedzi" description="Wyszukiwanie semantyczne po rzeczywistych transkrypcjach. System nigdy nie generuje cytatów, których nie ma w materiale." actions={<Button size="sm" onClick={() => void load()}>Odśwież</Button>}>
        {error ? <ErrorAlert error={error} /> : null}
        {message ? <div className="alert alert--success">{message}</div> : null}
        <div className="inline" style={{ gap: 8, flexWrap: 'wrap' }}>
          <input style={{ flex: 1, minWidth: 240 }} value={query} onChange={(event) => setQuery(event.target.value)} onKeyDown={(event) => { if (event.key === 'Enter') void search(); }} placeholder="np. „Nie mam pojęcia, co robię”" />
          <Button variant="primary" onClick={() => void search()} disabled={busy || !query.trim()}>Szukaj semantycznie</Button>
          {semantic && <span className="muted" style={{ fontSize: 12 }}>embeddingi aktywne</span>}
        </div>
        {hits.length > 0 && (
          <div style={{ marginTop: 12 }}>
            {hits.map((hit) => (
              <div key={hit.id} className="card" style={{ padding: 12, marginBottom: 8 }}>
                <div className="inline" style={{ justifyContent: 'space-between' }}><strong>{hit.speakerName ?? 'mówca'}</strong><span className="muted" style={{ fontSize: 11 }}>score {hit.score.toFixed(2)} · {formatMs(hit.durationMs)} · humor {Math.round(hit.humorScore)}</span></div>
                <div style={{ marginTop: 6 }}>„{hit.text}”</div>
                {hit.matchedTerms.length > 0 && <div className="muted" style={{ fontSize: 11, marginTop: 4 }}>dopasowane: {hit.matchedTerms.join(', ')}</div>}
              </div>
            ))}
          </div>
        )}
      </ComedySection>

      <ComedySection title="Materiały źródłowe i transkrypcje" description={`${total} aktywnych segmentów. Transkrypcja wykorzystuje lokalny whisper.cpp na VPS.`}>
        {loading ? <Spinner label="Ładowanie biblioteki…" /> : media.length === 0 ? (
          <ComedyEmpty title="Brak materiałów" hint="Wgraj własny plik lub pobierz materiał z potwierdzonymi prawami w zakładce Research AI." />
        ) : (
          <div className="table-wrap" style={{ overflowX: 'auto' }}>
            <table className="table">
              <thead><tr><th>Tytuł</th><th>Typ</th><th>Czas</th><th>Pobranie</th><th>Transkrypcja</th><th /></tr></thead>
              <tbody>
                {media.map((record) => (
                  <tr key={String(record.id)}>
                    <td><strong>{String(record.title ?? record.researchTitle ?? record.id)}</strong><div className="muted" style={{ fontSize: 11 }}>{String(record.mimeType ?? '')}</div></td>
                    <td>{String(record.kind ?? 'SOURCE_VIDEO')}</td>
                    <td>{record.durationSeconds ? `${Math.round(Number(record.durationSeconds))} s` : '—'}</td>
                    <td><ComedyStatus value={String(record.downloadStatus ?? '')} /></td>
                    <td><ComedyStatus value={String(record.transcriptionStatus ?? '')} />{record.failureReason ? <div className="muted" style={{ fontSize: 11, color: 'var(--danger)' }}>{String(record.failureReason).slice(0, 120)}</div> : null}</td>
                    <td><div className="btn-row"><Button size="sm" variant="primary" onClick={() => void transcribe(String(record.id))} disabled={busy || String(record.downloadStatus) !== 'READY'}>Transkrybuj</Button>{record.url ? <a className="btn btn--sm" href={String(record.url)} target="_blank" rel="noreferrer">Podgląd</a> : null}</div></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </ComedySection>

      <ComedySection title="Wgraj własny lub licencjonowany materiał" description="Deklaracja praw jest zapisywana wraz z identyfikatorem administratora. Bez CLEARED materiał nie wejdzie do produkcji.">
        <div className="form-row">
          <Field label="Plik (video/audio)"><input type="file" accept="video/*,audio/*" onChange={(event) => setUpload((current) => ({ ...current, file: event.target.files?.[0] ?? null }))} /></Field>
          <Field label="Tytuł"><input value={upload.title} onChange={(event) => setUpload((current) => ({ ...current, title: event.target.value }))} placeholder="np. Wywiad — odcinek 12" /></Field>
        </div>
        <div className="form-row">
          <Field label="Autor / mówca"><input value={upload.author} onChange={(event) => setUpload((current) => ({ ...current, author: event.target.value }))} /></Field>
          <Field label="URL źródła (opcjonalny)"><input value={upload.sourceUrl} onChange={(event) => setUpload((current) => ({ ...current, sourceUrl: event.target.value }))} /></Field>
        </div>
        <div className="form-row">
          <Field label="Podstawa praw">
            <select value={upload.rightsBasis} onChange={(event) => setUpload((current) => ({ ...current, rightsBasis: event.target.value }))}><option value="OWNED">OWNED — własne nagranie</option><option value="LICENSED">LICENSED — umowa licencyjna</option><option value="CREATIVE_COMMONS">CREATIVE_COMMONS</option><option value="PUBLIC_DOMAIN">PUBLIC_DOMAIN</option></select>
          </Field>
          <Field label="Mówca (opcjonalnie)"><input value={upload.speakerName} onChange={(event) => setUpload((current) => ({ ...current, speakerName: event.target.value }))} /></Field>
        </div>
        <div className="grid grid--3">
          <label className="inline"><input type="checkbox" checked={upload.commercialUse} onChange={(event) => setUpload((current) => ({ ...current, commercialUse: event.target.checked }))} /> Wykorzystanie komercyjne</label>
          <label className="inline"><input type="checkbox" checked={upload.modificationsAllowed} onChange={(event) => setUpload((current) => ({ ...current, modificationsAllowed: event.target.checked }))} /> Przeróbki / montaż</label>
          <label className="inline"><input type="checkbox" checked={upload.attributionRequired} onChange={(event) => setUpload((current) => ({ ...current, attributionRequired: event.target.checked }))} /> Wymagana atrybucja</label>
        </div>
        {upload.attributionRequired && <Field label="Treść atrybucji"><input value={upload.attributionText} onChange={(event) => setUpload((current) => ({ ...current, attributionText: event.target.value }))} /></Field>}
        <Button variant="primary" onClick={() => void doUpload()} disabled={busy || !upload.file}>{busy ? 'Wgrywanie…' : 'Wgraj materiał'}</Button>
      </ComedySection>

      <ComedySection title="Ostatnie segmenty" description="Segmenty są tworzone z rzeczywistych timestampów transkrypcji; mogą być filtrowane i ręcznie korygowane przez API.">
        {segments.length === 0 ? <ComedyEmpty title="Brak wypowiedzi" hint="Uruchom transkrypcję materiału, aby zbudować bibliotekę." /> : (
          <div className="table-wrap" style={{ overflowX: 'auto' }}>
            <table className="table">
              <thead><tr><th>Tekst</th><th>Mówca</th><th>Czas</th><th>Emocja</th><th>Humor</th><th>Użycia</th><th /></tr></thead>
              <tbody>
                {segments.map((segment) => (
                  <tr key={segment.id}>
                    <td style={{ maxWidth: 420 }}>„{segment.text}”</td>
                    <td>{segment.speakerName ?? '—'}</td>
                    <td>{formatMs(segment.startMs)}–{formatMs(segment.endMs)}</td>
                    <td>{segment.emotion ?? '—'}</td>
                    <td>{Math.round(segment.humorScore)}</td>
                    <td>{segment.usageCount}</td>
                    <td><Button size="sm" onClick={() => { const next = window.prompt('Popraw transkrypcję (cytaty muszą odpowiadać nagraniu):', segment.text); if (next && next.trim() && next !== segment.text) void api.comedy.updateSegment(channel.id, segment.id, { text: next.trim() }).then(() => load()).catch(setError); }}>Popraw</Button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </ComedySection>
    </div>
  );
}
